import { differenceInHours } from 'date-fns';
import { RentVerificationStatus } from 'src/api/rentVerificationApi';
import Theme from 'src/theme';

const MAX_PROCESSING_HOURS = 72;

export const getReportCellText = (status: RentVerificationStatus): string => {
  switch (status) {
    case RentVerificationStatus.PENDING:
      return "We're verifying the applicant's rental history now.";

    case RentVerificationStatus.FAILED:
      return 'We couldn’t process this request.';

    case RentVerificationStatus.EXPIRED:
      return 'We couldn’t retrieve rental references.';

    case RentVerificationStatus.CANCELLED:
      return 'This rental history request was cancelled.';

    case RentVerificationStatus.COMPLETED:
      // For completed status, we'll handle the download button in the component
      return '';

    default:
      return 'Report not available';
  }
};

export const calculateProcessingTime = (
  status: RentVerificationStatus,
  insertedAt: string,
  updatedAt?: string
): string => {
  // Database now returns proper ISO 8601 UTC timestamps
  const startTime = new Date(insertedAt);
  const currentTime = new Date();
  const endTime = updatedAt ? new Date(updatedAt) : currentTime;

  // Ensure we're calculating positive hours and cap at 0 minimum
  const calculateHours = (end: Date, start: Date): number => {
    return Math.max(0, Math.min(differenceInHours(end, start), MAX_PROCESSING_HOURS));
  };

  const formatHoursCopy = (hours: number): string => {
    if (hours === 0) return 'less than 1 hour';
    return hours === 1 ? '1 hour' : `${hours} hours`;
  };

  let hours: number;

  switch (status) {
    case RentVerificationStatus.PENDING:
      // Hours since submission (capped at 72, minimum 0)
      hours = calculateHours(currentTime, startTime);
      return formatHoursCopy(hours);

    case RentVerificationStatus.FAILED:
      // Technical Issue: always 0 hours
      return '0 hours';

    case RentVerificationStatus.COMPLETED:
      // Hours from submission to completion (capped at 72, minimum 0)
      hours = calculateHours(endTime, startTime);
      return formatHoursCopy(hours);

    case RentVerificationStatus.EXPIRED:
      // Time Expired: always 72 hours
      return `${MAX_PROCESSING_HOURS} hours`;

    case RentVerificationStatus.CANCELLED:
      // Hours from submission to cancellation (capped at 72, minimum 0)
      hours = calculateHours(endTime, startTime);
      return formatHoursCopy(hours);

    default:
      return '0 hours';
  }
};

export const getStatusColor = ({
  theme,
  status
}: {
  theme: typeof Theme;
  status: RentVerificationStatus;
}) => {
  switch (status) {
    case RentVerificationStatus.COMPLETED:
      return `color: ${theme.color.green[600]};
        background-color: ${theme.color.green[50]}
      `;
    case RentVerificationStatus.PENDING:
      return `color: ${theme.color.blue[400]};
        background-color: ${theme.color.blue[80]}
      `;
    case RentVerificationStatus.CANCELLED:
    case RentVerificationStatus.EXPIRED:
      return `color: ${theme.color.gray[600]};
        background-color: ${theme.color.gray[200]}
      `;
    case RentVerificationStatus.FAILED:
      return `color: ${theme.color.yellow[600]};
        background-color: ${theme.color.yellow[50]}
      `;
    default:
      return theme.color.gray[300];
  }
};

const toTitleCase = (status: RentVerificationStatus): string =>
  status
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const formatStatusName = (status: RentVerificationStatus): string => {
  switch (status) {
    case RentVerificationStatus.FAILED:
      return 'Technical Issue';
    case RentVerificationStatus.PENDING:
      return 'In Progress';
    case RentVerificationStatus.EXPIRED:
      return 'Time Expired';
    case RentVerificationStatus.COMPLETED:
      return 'Complete';
    default:
      return toTitleCase(status);
  }
};
